<template>
    <div class="lg:max-w-32 grid grid-cols-2 lg:block">
        <span class="block lg:hidden text-xs text-slate-500">{{ metricAbbreviation(props.metric) }}:</span>

        <vx-input
            v-model="target"
            :disabled="disabled"
            :class="{
                'opacity-50': disabled,
            }"
            :error="displayFirstError(validation.target)"
        >
            <template v-slot:right>
                <font-awesome-icon
                    widthAuto
                    class="text-xs"
                    :class="{
                        'opacity-50': disabled,
                    }"
                    :icon="faPercent"
                />
            </template>
        </vx-input>
    </div>
</template>

<script setup>
import { faPercent } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxInput } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { maxValue, minValue, required, decimal, helpers } from '@vuelidate/validators';
import { metricAbbreviation } from '~/utils/analytics';
import { displayFirstError } from '~/utils/validation';

const metrics = defineModel({
    type: Object,
    required: true,
});

const props = defineProps({
    category: {
        type: String,
        required: true,
    },
    metric: {
        type: String,
        required: true,
    },
});

const target = ref();

const setInitialTarget = () => {
    const value = metrics.value?.[props.category]?.[props.metric];
    target.value = value !== undefined ? String(parseFloat((value * 100).toFixed(4))) : undefined;
};

const disabled = computed(() => target.value === undefined);

watch(target, (newValue, oldValue) => {
    if (oldValue === undefined) {
        return;
    }

    const decimalValue = parseFloat(newValue) / 100;
    metrics.value[props.category][props.metric] = parseFloat(decimalValue.toFixed(6));
});

const rules = computed(() => ({
    target: disabled.value
        ? {} // No validation when disabled
        : {
              required,
              decimal,
              minValue: minValue(0),
              maxValue: maxValue(100),
              regex: helpers.withMessage('Must have no more than 4 decimal places', helpers.regex(/^\d+(\.\d{1,4})?$/)),
          },
}));

const validation = useVuelidate(rules, { target: target });

setInitialTarget();
</script>
